import { feathers } from '@/feathers-client';
import { HIDE_ERROR_AFTER_MS, CANCEL_DIRTY_TIMEOUT_MS } from '@/Core/utilities/index';
import QueryBuilderFactory from '@/Core/utilities/filter-query-builders/QueryBuilderFactory';
import availableContactFilterFields, {
  createCustomFieldFilter,
} from '@/Contacts/config/available-contact-filter-fields';
import { ADDRESSES_FIELD } from '@/Contacts/config/table-headers';
import { DefaultSlippingAwayDays } from '@/Contacts/utilities';
import { AND_CONDITION, OR_CONDITION } from '@/Core/utilities/filter-query-builders/constants';

const DEFAULT_LIMIT_CONTACT_FILTERS = 50;
const saveErrorsDefault = [];
const selectedFilterDefault = {};
const findErrorMessage = 'Could not load saved filters; refresh to try again.';
const filtersExpansionDefault = false;

const FieldNames = Object.freeze({
  Keywords: 'keywords',
});

const parseContactFiltersData = (contactFiltersData) => {
  return contactFiltersData.map(filter => {
    const conditions = JSON.parse(filter.conditions).map(condition => {
      return transformCondition(condition);
    });
    filter.conditions = JSON.stringify(conditions);
    return filter;
  });
};

/**
 * TODO MW - put a migration strategy in place for changes to the contact filter fields
 * transform to conditions based on field name changes that occurred
 * June 20th, 2019
 *
 * @param {Object} condition filter condition
 */
export function transformCondition(condition) {
  if (condition.type !== undefined) {
    condition.queryType = condition.type;
    condition.filterComponent = condition.type;
    delete condition.type;
    if (['multiselect', 'hotness'].includes(condition.queryType)) {
      condition.queryType = 'exact-match';
    }
    if (condition.field === 'tag_names') {
      condition.queryType = 'caret-delimited';
      condition.operator =
        condition.operator === 'contain any of' ? 'include any of' : condition.operator;
      condition.operator =
        condition.operator === 'contain all of' ? 'include all of' : condition.operator;
    }
  }
  return condition;
}

/**
 * TODO CS - this logic will likely have to exist on the back-end as well
 * so the back-end can use the contact filters to generate lists of IDs
 * to operate on.
 */
export function addToQueryObject(condition, query, conditionType, index) {
  /**
   * TODO CS - condition.type has been deprecated but exists in the users' saved filters.
   *           Left here until that json migration is run.
   */
  const queryType = condition.queryType || condition.type;
  if (queryType === 'keywords') return query;

  const queryBuilder = new QueryBuilderFactory(queryType).generate(condition.operator, query);
  if (!queryBuilder || !queryBuilder.QueryObject) {
    throw new Error(`Could not find a query builder for type ${queryType}`);
  }

  if (queryType === 'text') {
    const fields = queryBuilder.ConvertFieldNameToArray(condition.field)
    if (!queryBuilder.HasOperator() || (condition.requiresOperand && !condition.value.trim())) {
      return query;
    }
    return { ...queryBuilder.QueryObject(fields, condition.value, conditionType, index) };
  }

  return { ...queryBuilder.QueryObject(condition.field, condition.value, conditionType, index) };
}

function addToQuery(queries, groupConditionType, subquery) {
  // Handle the '$or' group condition
  if (groupConditionType === '$or') {
    queries.$and.forEach(query => handleOrCondition(query, subquery));
    return;
  }
  // Handle the '$and' group condition when '$or' is present
  else if (groupConditionType === '$and' && queries.$and.some(query => query.$or)) {
    queries.$and.forEach(query => handleAndConditionWithOr(query, subquery));
    return;
  }
  queries.$and.forEach(query => handleAndCondition(query, subquery));
  return
}

function handleOrCondition(query, subquery) {
  if (query.$or) {
    query.$or.push(subquery);
    return;
  } else if (query.$and) {
    transformAndToOr(query, subquery);
    return;
  }
  query.$or = [subquery];
  return;
}

function handleAndConditionWithOr(query, subquery) {
  if (!query.$or.some(cond => cond.$and)) {
    query.$or.push({ $and: [subquery] });
    return
  }
  query.$or.find(cond => cond.$and).$and.push(subquery);
  return;
}

function handleAndCondition(query, subquery) {
  if (query.$and) {
    query.$and.push(subquery);
    return;
  }
  query.$and = [subquery];
  return;
}

function transformAndToOr(query, subquery) {
  query.$or = [{ $and: [...query.$and] }, subquery];
  delete query.$and;
}

export default {
  namespaced: true,
  state: {
    // Edited and not yet saved
    isDirty: true,
    // Saving in progress
    isSaving: false,
    // Filters pop-down is visible
    isExpanded: filtersExpansionDefault,
    isOnDirtyTimeout: false,

    findErrors: null,
    saveErrors: saveErrorsDefault,

    selectedFilter: selectedFilterDefault,
    contactFilters: [],

    // The actual conditions contributing to the
    // feathers query being what it is.
    activeConditions: [],
    isDisplayedOnDashboard: false,
    dashboardContactsSlippingAway: false,
    availableContactFilterFields,
    dashboardFiltersCount: 0,
    applyContactsSlippingAwayConditions: false,
    slippingAwayDays: DefaultSlippingAwayDays,
    hasMoreContactFiltersToLoad: false,
  },
  getters: {
    hasSaveErrors(state) {
      return state.saveErrors.length > 0;
    },

    hasFindErrors(state) {
      return state.findErrors;
    },

    filterQueries(state) {
      const queries = {};
      const subqueries = state.activeConditions.map(groupCondition => {
        let queryGroup = {};

        groupCondition.forEach((condition, index) => {
          queryGroup = addToQueryObject(condition, queryGroup, condition.conditionType, index);
        });

        return { groupConditionType: groupCondition[0].groupConditionType, ...queryGroup };
      });
      subqueries.forEach((subquery, index) => {
        const groupConditionType = `$${subquery.groupConditionType}`.toLowerCase();
        delete subquery.groupConditionType;

        if (index === 0) {
          queries.$and = [{ ...subquery }];
        } else {
          if (index === 1) {
            queries.$and = [{ [groupConditionType]: [queries.$and[0], subquery] }];
          } else {
            addToQuery(queries, groupConditionType, subquery);
          }
        }
      });

      return queries;
    },

    haveConditionsBeenSet(state, getters) {
      const query = { ...getters.filterQueries };
      delete query.search;
      return Object.keys(query).length > 0;
    },

    /**
     * Finds the index of the activecondition with { type: 'keywords' }
     *
     * Returns -1 if not found
     */
    searchConditionIndex(state) {
      return state.activeConditions.findIndex(condition => {
        return [condition.type, condition.queryType].includes(FieldNames.Keywords);
      });
    },
  },
  mutations: {
    setContactFilters(state, contactFilters) {
      state.contactFilters = parseContactFiltersData(contactFilters.data);
    },

    appendContactFilters(state, contactFilters) {
      state.contactFilters = [
        ...state.contactFilters,
        ...parseContactFiltersData(contactFilters.data)
      ];
    },

    setHasMoreContactFiltersToLoad(state, hasMoreContactFiltersToLoad) {
      state.hasMoreContactFiltersToLoad = hasMoreContactFiltersToLoad;
    },

    setSelectedFilter(state, contactFilter) {
      state.selectedFilter = contactFilter;
      state.isDisplayedOnDashboard = !!contactFilter.display_on_dashboard;
      state.dashboardContactsSlippingAway = !!contactFilter.show_contacts_slipping_away;
      state.slippingAwayDays = parseInt(contactFilter.slipping_away_days, 10);
    },

    startLoading(state) {
      state.isSaving = true;
    },

    stopLoading(state) {
      state.isSaving = false;
    },

    /**
     * Iterate the list and replace the old item with matching ID
     *
     * @param  {object} state
     * @param  {object} newFilter - the new filter to replace with
     */
    replaceListItem(state, newFilter) {
      state.contactFilters = state.contactFilters.map(filter => {
        if (newFilter.id === filter.id) {
          return newFilter;
        }
        return filter;
      });
    },

    addNew(state, filter) {
      state.contactFilters = [filter, ...state.contactFilters];
    },
    removeFilter(state, filterId) {
      state.contactFilters = state.contactFilters.filter(filter => {
        return filter && filter.id !== filterId;
      });
    },
    setSaveError(state, errors) {
      state.saveErrors = errors;
      state.isSaving = false;
    },
    hideSaveError(state) {
      state.saveErrors = saveErrorsDefault;
      state.isSaving = false;
    },
    setFindError(state) {
      state.findErrors = findErrorMessage;
    },
    setDirty(state, isDirty) {
      if (state.isOnDirtyTimeout) return;
      state.isDirty = isDirty;
    },
    // Dirty state is kept off around the time that a saved filter is selected.
    // This means that any change to the saved filter drop-down won't cause a dirty
    // state, but any change to the contents will.
    // Necessary because the table-filters can't know whether the parameters changed due
    // to condition input, or the select was simply changed.
    cancelDirtyTimeout(state) {
      state.isOnDirtyTimeout = false;
    },
    setDirtyTimeout(state) {
      state.isOnDirtyTimeout = true;
    },
    hideFindError(state) {
      state.findErrors = null;
    },
    toggleIsExpanded(state) {
      state.isExpanded = !state.isExpanded;
    },
    replaceActiveConditions(state, activeConditions) {
      state.activeConditions = activeConditions;
    },
    updateActiveCondition(state, { index, subqueryIndex, activeCondition }) {
      // The filter before its value comes in
      const prev = state.activeConditions[index][subqueryIndex];

      // Update the filter with a built query object contained inside
      const updatedFilter = Object.assign(prev, activeCondition);

      // Replace the updated activeFilter at a certain index
      state.activeConditions[index].splice(subqueryIndex, 1, updatedFilter);

      // For reactivity, assign this object back to itself.
      state.activeConditions[index][subqueryIndex] = Object.assign(
        {},
        state.activeConditions[index][subqueryIndex],
        activeCondition,
      );
    },
    addActiveCondition(state, { groupConditionIndex, condition }) {
      state.activeConditions[groupConditionIndex].push(Object.assign({}, condition));
    },
    addNewActiveCondition(state, { index, conditionType }) {
      const activeCondition = { ...availableContactFilterFields[0], conditionType };

      // Ensure an array exists at the specified index
      state.activeConditions[index] = state.activeConditions[index] || [];

      // Determine the insertion index for AND_CONDITION
      let insertionIndex = state.activeConditions[index].length;
      if (conditionType === AND_CONDITION) {
        const firstOrIndex = state.activeConditions[index].findIndex(cond => cond.conditionType === OR_CONDITION);
        if (firstOrIndex !== -1) {
          insertionIndex = firstOrIndex;
        }
      }

      // Insert the new condition at the determined index
      state.activeConditions[index].splice(insertionIndex, 0, activeCondition);
    },
    addNewConditionGroup(state, groupConditionType) {
      // Create a new condition group
      const newConditionGroup = [{ groupConditionType, ...availableContactFilterFields[0] }];

      if (groupConditionType === AND_CONDITION) {
        // Find the first OR condition index
        const firstOrIndex = state.activeConditions.findIndex(cond => {
          return cond[0].groupConditionType === OR_CONDITION;
        });

        if (firstOrIndex > 0) {
          // Insert before the first OR condition if found and not at the start
          state.activeConditions.splice(firstOrIndex, 0, newConditionGroup);
          return;
        }
      }

      // For OR_CONDITION, and as a fallback for AND_CONDITION, push at the end
      state.activeConditions.push(newConditionGroup);
    },
    replaceActiveCondition(state, { index, condition: searchCondition }) {
      state.activeConditions[index] = searchCondition;
    },

    /**
     * Load resource onto array.
     *
     * With a list of not-yet populated filters, we allow later joining of
     * these resources onto the filters.
     *
     * For example, the tags a user may select from come from a list.
     * This list is loaded asynchronously, and the user may see/use the
     * filters in the meantime.
     *
     * Once the tags are found, they're loaded onto the filters using
     * this function.
     *
     * The TableFilters component watches for changes on the availableFilters
     * as well as the activeConditions and updates their lists as needed.
     *
     * ---
     *
     * Looks for a key `loadWith` on each object of the list. If found,
     * it loads the data into the options key.
     *
     * @param {array} listToFix
     * @param {string} resourceName like 'tags'
     * @param {array} resource like ['tag1', 'tag2']
     */
    loadWithResource(state, { resource, resourceName }) {
      state.activeConditions =  [ ...state.activeConditions.map(conditions => {
        return conditions.map(condition => {
          if (condition.loadWith === resourceName) {
            const updatedFilterField = { ...condition };
            updatedFilterField.options = resource;
            return updatedFilterField
          }
          return condition;
        });
      })];

      state.availableContactFilterFields = [ ...state.availableContactFilterFields.map(condition => {
        if (condition.loadWith === resourceName) {
          const updatedFilterField = { ...condition };
          updatedFilterField.options = resource;
          return updatedFilterField
        }
        return condition
      })];
    },

    /**
     * Add the custom fields to the available contact filter fields
     *
     * @param   {object}  state - vuex state
     * @param   {array}  customFields  the custom fields to add
     */
    addCustomFields(state, customFields) {
      const customFieldFilters = customFields.map(customField => {
        const customFieldApiKey = `custom_field_${customField.id}`;
        return createCustomFieldFilter({
          name: customField.name,
          field: customFieldApiKey,
          dataType: customField.data_type,
        });
      });

      const insertAboveFieldIndex = state.availableContactFilterFields.findIndex(
        filter => filter.field === ADDRESSES_FIELD,
      );

      state.availableContactFilterFields.splice(insertAboveFieldIndex, 0, ...customFieldFilters);
    },

    removeCondition(state, { groupConditionIndex, subqueryIndex, index }) {
      state.activeConditions[groupConditionIndex].splice(subqueryIndex, 1);
      if (state.activeConditions[groupConditionIndex].length == 0) {
        state.activeConditions.splice(groupConditionIndex, 1)
      }
    },
    removeGroupCondition(state, groupConditionIndex) {
      state.activeConditions.splice(groupConditionIndex, 1);
    },
    setDisplayOnDashboard(state, displayOnDashboard) {
      state.isDisplayedOnDashboard = displayOnDashboard;
    },
    setDashboardFiltersCount(state) {
      state.dashboardFiltersCount = state.contactFilters.filter(contactFilter => {
        return contactFilter.display_on_dashboard;
      }).length;
    },
    setDashboardContactsSlippingAway(state, dashboardContactsSlippingAway) {
      state.selectedFilter.show_contacts_slipping_away = dashboardContactsSlippingAway;
    },
    setSlippingAwayFilter(state, applyContactsSlippingAwayConditions) {
      state.applyContactsSlippingAwayConditions = applyContactsSlippingAwayConditions;
    },
    setSlippingAwayDays(state, days) {
      state.slippingAwayDays = parseInt(days, 10);
      state.selectedFilter.slipping_away_days = parseInt(days, 10);
    },
  },
  actions: {
    toggleIsExpanded({ state, commit }) {
      commit('toggleIsExpanded');
    },
    setSelected({ commit }, selectedFilter) {
      commit('setSelectedFilter', JSON.parse(JSON.stringify(selectedFilter)));
      commit('setDirty', false);
      setTimeout(() => {
        commit('cancelDirtyTimeout');
      }, 1000);
    },

    clearSelected({ commit }) {
      commit('setSelectedFilter', selectedFilterDefault);
    },

    setDirty({ commit }) {
      commit('setDirty', true);
    },

    clearDirty({ commit }) {
      commit('setDirty', false);
    },

    updateActiveCondition({ state, commit }, { index, subqueryIndex, activeCondition }) {
      commit('updateActiveCondition', { index, subqueryIndex, activeCondition });
      commit('setDirty', true);
    },

    replaceActiveConditions({ commit, dispatch }, activeConditions) {
      commit('replaceActiveConditions', activeConditions);
      dispatch('contactsTable/pagination/setPage', 1, { root: true });
    },

    addActiveCondition({ commit }, condition) {
      commit('addActiveCondition', { condition });
    },

    addNewActiveCondition({ commit }, { index, conditionType }) {
      commit('addNewActiveCondition', { index, conditionType });
    },

    addNewConditionGroup({ commit }, groupConditionType) {
      commit('addNewConditionGroup', groupConditionType);
    },

    loadWithResource({ commit }, { resource, resourceName }) {
      commit('loadWithResource', { resource, resourceName });
    },

    addCustomFields({ commit }, customFields) {
      commit('addCustomFields', customFields);
    },

    removeCondition({ state, commit }, { groupConditionIndex, subqueryIndex, index }) {
      commit('removeCondition', { groupConditionIndex, subqueryIndex, index });
      commit('setDirty', true);
    },

    removeGroupCondition({ state, commit }, index) {
      commit('removeGroupCondition', index);
      commit('setDirty', true);
    },

    updateSearchCondition({ state, getters, commit, dispatch }, searchTerm) {
      const searchCondition = {
        name: 'Keywords',
        field: 'search',
        type: FieldNames.Keywords,
        operator: 'contains',
        value: searchTerm,
      };

      const { searchConditionIndex } = getters;

      if (searchConditionIndex === -1) {
        commit('addActiveCondition', { condition: searchCondition });
      } else {
        commit('replaceActiveCondition', {
          index: searchConditionIndex,
          condition: searchCondition,
        });
      }

      commit('setDirty', true);
    },

    async find({ commit, state }, offset = 0) {
      try {
        // TODO CS - make this work when filters > 50
        // TODO CS - make a global constant for max pagination = 50
        const contactFilters = await feathers.service('contact-filters').find({
          query: {
            $limit: DEFAULT_LIMIT_CONTACT_FILTERS,
            $skip: offset,
            $sort: { id: -1 },
          },
        });

        if (offset == 0) {
          commit('setContactFilters', contactFilters);
        } else {
          commit('appendContactFilters', contactFilters);
        }
        commit('setHasMoreContactFiltersToLoad', contactFilters.total > state.contactFilters.length);
        commit('setDashboardFiltersCount');
      } catch (error) {
        commit('setFindError');
      }
    },
    async patch({ commit, getters }, contactFilter) {
      commit('startLoading');
      const { id, ...restOfContactFilter } = contactFilter;
      try {
        const response = await feathers.service('contact-filters').patch(id, restOfContactFilter);
        if (response && response.id) {
          commit('replaceListItem', response);
          commit('setSelectedFilter', response);
          commit('setDirty', false);
          setTimeout(() => {
            commit('cancelDirtyTimeout');
          }, CANCEL_DIRTY_TIMEOUT_MS);
          commit('stopLoading');
          commit('setDashboardFiltersCount');
        }
      } catch (error) {
        commit('setSaveError', error.errors);
        commit('stopLoading');
        setTimeout(() => {
          commit('hideSaveError');
        }, HIDE_ERROR_AFTER_MS);
      }
    },

    async create({ commit }, newContactFilter) {
      commit('startLoading');
      try {
        const contactFilter = await feathers.service('contact-filters').create(newContactFilter);
        if (contactFilter && contactFilter.id) {
          commit('addNew', contactFilter);
          commit('setSelectedFilter', contactFilter);
          commit('stopLoading');
          commit('setDirty', false);
          commit('setDashboardFiltersCount');
          return contactFilter;
        }
      } catch (error) {
        commit('setSaveError', error.errors);
        commit('stopLoading');
        setTimeout(() => {
          commit('hideSaveError');
        }, HIDE_ERROR_AFTER_MS);
      }
    },

    async delete({ commit }, contactFilterId) {
      try {
        const contactFilter = await feathers.service('contact-filters').remove(contactFilterId);
        if (contactFilter && contactFilter.id) {
          commit('setSelectedFilter', selectedFilterDefault);
          commit('removeFilter', contactFilter.id);
        }
        commit('setDashboardFiltersCount');
        commit('setDirty', true);
      } catch (error) {
        commit('setSaveError', [error.message]);
        setTimeout(() => {
          commit('hideSaveError');
        }, HIDE_ERROR_AFTER_MS);
        throw error;
      }
    },

    setDisplayOnDashboard({ commit }, status) {
      commit('setDirty', true);
      if (!status) {
        commit('setDashboardContactsSlippingAway', false);
      }
      commit('setDisplayOnDashboard', status);
    },
    setDashboardContactsSlippingAway({ state, commit }, status) {
      commit('setDirty', true);
      if (status && !state.isDisplayedOnDashboard) {
        commit('setDisplayOnDashboard', true);
      }
      commit('setDashboardContactsSlippingAway', status);
    },
    toggleShowContactsSlippingAway({ commit }, applyContactsSlippingAwayConditions) {
      commit('setSlippingAwayFilter', applyContactsSlippingAwayConditions);
    },
    setSlippingAwayDays({ state, commit, dispatch }, days) {
      commit('setDirty', true);
      commit('setSlippingAwayDays', days);
    },
  },
};
